'use client'

import {IntentLink as Link} from '@/components/IntentLink'
import {Logo} from '@/components/brand/Logo'
import {useShellText} from '@/components/shell/ShellText'
import {useEffect, useRef, useState} from 'react'
import {LogOut, Settings} from 'lucide-react'
import {LangSwitch} from '@/design-system'
import {api} from '@/lib/api/client'
import {APP_LOCALES} from '@/lib/locales'
import type {Locale, Me} from '@/lib/api/types'
import {useLocaleSwitch} from '@/hooks/useLocaleSwitch'
import {ThemeToggle} from './ThemeToggle'

/**
 * Sticky app header (T-WEB-08).
 *
 * Reuses the landing header's wordmark and `LangSwitch` so the two chromes
 * read as one product. The switcher offers every locale in `APP_LOCALES` —
 * all three, now that `ru.json` carries the app namespaces and not just the
 * landing one. Anything offered here must have a full catalogue behind it:
 * a language that silently falls back to Uzbek is worse than no option.
 */
export function AppHeader({me, locale}: {me: Me; locale: Locale}) {
  const t = useShellText('shell')
  const [menuOpen, setMenuOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const {value: chosenLocale, pending: switching, switchTo} = useLocaleSwitch(locale)

  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      // Even if the call failed, sending them to the landing page is the
      // right end state: the cookie is either cleared or about to be.
      window.location.href = `/${locale}`
    }
  }

  // A disclosure, not an ARIA menu: two plain actions don't need roving
  // focus, but they do need Escape, outside-click and focus return.
  useEffect(() => {
    if (!menuOpen) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setMenuOpen(false)
      triggerRef.current?.focus()
    }
    function onPointer(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [menuOpen])

  return (
    <header className="u-app__header">
      <div className="u-app__bar">
        <Link href={`/${locale}/dashboard`} className="u-app__wordmark">
          <Logo />
        </Link>

        <div className="u-app__actions">
          <LangSwitch
            value={chosenLocale}
            onChange={switchTo}
            languages={APP_LOCALES}
            label={t('languageLabel')}
            pending={switching}
          />

          <ThemeToggle />

          <div
            ref={wrapRef}
            className="u-app__account"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setMenuOpen(false)
            }}
          >
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="account-panel"
              aria-label={t('accountMenu')}
              className="u-app__avatar"
            >
              {me.avatar_url ? (
                // Not next/image: Google's avatar host would need a remote
                // pattern allowlist and an optimiser round trip for a 32px
                // image already sized by the CDN.
                // eslint-disable-next-line @next/next/no-img-element
                <img src={me.avatar_url} alt="" width={32} height={32} />
              ) : (
                <span aria-hidden="true">{(me.name ?? me.email).slice(0, 1).toUpperCase()}</span>
              )}
            </button>

            {menuOpen ? (
              <div id="account-panel" className="u-app__panel">
                <div className="u-app__identity">
                  {me.name ? <span className="u-app__identity-name">{me.name}</span> : null}
                  <span className="u-app__identity-email">{me.email}</span>
                </div>
                <Link
                  href={`/${locale}/settings`}
                  className="u-app__navlink"
                  onClick={() => setMenuOpen(false)}
                >
                  <Settings aria-hidden="true" size={18} />
                  {t('settings')}
                </Link>
                <button type="button" onClick={logout} className="u-app__navlink u-app__navlink--button">
                  <LogOut aria-hidden="true" size={18} />
                  {t('logout')}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  )
}
